import { notify, useMusicStand } from '@church/data'
import { COMMON_KEYS, roadmap, taggedKeyLabel } from '@church/domain'
import {
  faArrowLeft,
  faArrowRight,
  faCirclePause,
  faCirclePlay,
  faMinus,
  faMoon,
  faPlus,
  faSun,
  faTowerBroadcastDuotone,
} from '@church/ui-icons'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { FollowStatus } from '../components/services/FollowStatus.tsx'
import { KeyTags } from '../components/services/KeyPicker.tsx'
import { ChartView } from '../components/services/ChartView.tsx'
import { Icon, IconButton } from '../components/ui.tsx'
import { socketUrl } from '../lib/kiosk.ts'
import { documentTitle, useDocumentTitle } from '../lib/document-title.ts'

/**
 * The Music Stand: one chart at a time, big, in the band's key and the
 * player's capo, scrolling on its own if asked, dark by default for a stage —
 * and following the leader through the service.
 *
 * Everything here works without a network once the plan has been opened: the
 * plan and all its charts are one cached answer, and transposition is
 * computed on the device.
 */
export const Route = createFileRoute('/_authed/stand/$planId')({
  component: MusicStand,
})

function MusicStand() {
  const { planId } = Route.useParams()
  const stand = useMusicStand(planId, {
    followUrl: socketUrl(`/api/v1/services/plans/${planId}/follow`),
  })
  const [dark, setDark] = useState(true)
  const [scrolling, setScrolling] = useState(false)
  const page = useRef<HTMLDivElement>(null)
  useDocumentTitle(documentTitle('Music Stand', 'ForServices'))

  // Auto-scroll: a slow, steady glide for hands that are busy playing.
  useEffect(() => {
    if (!scrolling) return
    const timer = setInterval(() => page.current?.scrollBy({ top: 1 }), 60)
    return () => clearInterval(timer)
  }, [scrolling])
  // Braces: scrollTo returns a promise in current Chrome, and an effect must
  // return nothing but a cleanup function.
  useEffect(() => {
    page.current?.scrollTo({ top: 0 })
  }, [stand.index])

  // `light` too: the app may be in dark mode, and the page must still turn light.
  const theme = dark ? 'dark bg-black text-white' : 'light bg-surface-base text-content-default'

  if (stand.isLoading) return <div className={`min-h-screen p-8 ${theme}`}>Loading the plan…</div>
  if (!stand.plan) {
    return (
      <div className={`min-h-screen p-8 ${theme}`}>
        This plan has not been opened on this device yet, and there is no connection to fetch it.
      </div>
    )
  }
  const current = stand.current
  const leaderTitle = stand.items.find((item) => item.id === stand.leaderItemId)?.title ?? null
  /** Moving by hand stops following the leader: say so, with the way back. */
  const go = (next: number) => {
    if (stand.following && stand.leaderItemId)
      notify({
        title: 'Stopped following the leader',
        description: 'You’re moving through the plan yourself now.',
        action: { label: 'Rejoin', run: () => stand.setFollowing(true) },
      })
    stand.go(next)
  }

  return (
    // dvh, not vh: a phone's browser bars would otherwise hide the next/previous footer.
    <div className={`safe-area flex h-dvh flex-col ${theme}`} data-testid="music-stand">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-white/10 px-4 py-1 text-sm">
        <Link
          to="/services/plans/$id"
          params={{ id: planId }}
          aria-label="Back to the plan"
          title="Back to the plan"
          className="-ml-2 inline-flex size-10 items-center justify-center rounded-lg opacity-70 hover:bg-white/10"
        >
          <Icon icon={faArrowLeft} />
        </Link>
        <span className="font-semibold">
          {new Date(`${stand.plan.date}T12:00:00Z`).toLocaleDateString(undefined, {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
          })}
        </span>
        <span className="opacity-70">
          {stand.index + 1} / {stand.items.length}
        </span>
        <span className="flex-1" />
        <FollowStatus
          following={stand.following}
          connected={stand.connected}
          leaderTitle={leaderTitle}
          onFollow={stand.setFollowing}
        />
        {current?.chart && (
          <>
            <span className="flex items-center gap-1">
              Key
              <IconButton
                icon={faMinus}
                label="Down a semitone"
                className="text-current hover:bg-white/10"
                onClick={() => stand.stepKey(-1)}
              />
              <select
                value={stand.playKey ?? ''}
                onChange={(event) => stand.setKey(event.target.value)}
                className="min-h-10 rounded bg-transparent px-1 font-bold"
                aria-label="Key"
              >
                {/* The arrangement's own keys first, with who they are for. */}
                {stand.taggedKeys.length > 0 && (
                  <optgroup label="This arrangement" className="text-black">
                    {stand.taggedKeys.map((key) => (
                      <option key={key.key} value={key.key} className="text-black">
                        {taggedKeyLabel(key)}
                      </option>
                    ))}
                  </optgroup>
                )}
                <optgroup label="All keys" className="text-black">
                  {[...new Set([...(stand.playKey ? [stand.playKey] : []), ...COMMON_KEYS])]
                    .filter((key) => !stand.taggedKeys.some((tagged) => tagged.key === key))
                    .map((key) => (
                      <option key={key} value={key} className="text-black">
                        {key}
                      </option>
                    ))}
                </optgroup>
              </select>
              <IconButton
                icon={faPlus}
                label="Up a semitone"
                className="text-current hover:bg-white/10"
                onClick={() => stand.stepKey(1)}
              />
            </span>
            <span className="flex items-center gap-1">
              Capo
              <select
                value={stand.capo}
                onChange={(event) => stand.setCapo(Number(event.target.value))}
                className="min-h-10 rounded bg-transparent px-1"
                aria-label="Capo"
              >
                {Array.from({ length: 10 }, (_, fret) => (
                  <option key={fret} value={fret} className="text-black">
                    {fret === 0 ? 'none' : fret}
                  </option>
                ))}
              </select>
              {stand.shapesKey && <span className="opacity-70">({stand.shapesKey} shapes)</span>}
            </span>
            <button
              type="button"
              aria-pressed={stand.numbers}
              onClick={() => stand.setNumbers(!stand.numbers)}
              title="Chords as numbers (1 4 5) instead of letters"
              className={`inline-flex min-h-10 items-center rounded-lg px-3 ${stand.numbers ? 'bg-white/15 font-semibold' : 'opacity-80 hover:bg-white/10'}`}
            >
              1 4 5
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => setScrolling(!scrolling)}
          aria-pressed={scrolling}
          className={`inline-flex items-center gap-2 rounded-lg px-2 py-2 ${scrolling ? 'bg-white/15' : 'hover:bg-white/10'}`}
        >
          <Icon icon={scrolling ? faCirclePause : faCirclePlay} />
          {scrolling ? 'Stop' : 'Scroll'}
        </button>
        <IconButton
          icon={dark ? faSun : faMoon}
          label={dark ? 'Light page' : 'Dark page'}
          className="text-current hover:bg-white/10"
          onClick={() => setDark(!dark)}
        />
      </header>

      <div className="flex min-h-0 flex-1">
        <nav
          className="hidden w-60 shrink-0 overflow-auto border-r border-white/10 md:block"
          aria-label="Setlist"
        >
          {stand.items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => go(i)}
              className={`block w-full px-4 py-2 text-left text-sm ${i === stand.index ? 'bg-white/15 font-semibold' : 'opacity-80 hover:bg-white/5'} ${item.kind === 'header' ? 'text-xs font-bold uppercase tracking-wider text-accent-content' : ''}`}
            >
              {item.title}
              {item.id === stand.leaderItemId && (
                <Icon
                  icon={faTowerBroadcastDuotone}
                  label="The leader is here"
                  className="ml-2 inline size-3.5 text-content-brand"
                />
              )}
            </button>
          ))}
        </nav>
        <main ref={page} className="min-h-0 flex-1 overflow-auto px-4 py-4 sm:px-8 sm:py-6">
          {current && (
            <>
              <h1 className="text-2xl font-bold sm:text-3xl">{current.title}</h1>
              <p
                className={`flex flex-wrap items-center gap-x-2 gap-y-1 ${stand.sequence.length > 0 ? 'mb-3' : 'mb-6'}`}
              >
                <span className="opacity-70">
                  {[
                    current.author,
                    stand.playKey ? `Key ${stand.playKey}` : null,
                    current.meter,
                    current.lengthSeconds ? `${Math.round(current.lengthSeconds / 60)} min` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                {/* Whose key it is (Female, Low…), so nobody starts in the wrong one. */}
                <KeyTags tags={stand.playKeyTags} />
              </p>
              {/* The roadmap: the order the band plays it in. */}
              {stand.sequence.length > 0 && (
                <p
                  className="mb-6 text-lg font-bold tracking-wide"
                  title={stand.sequence.join(', ')}
                >
                  {roadmap(stand.sequence)}
                </p>
              )}
              {current.chart ? (
                <ChartView lines={stand.lines} layout={stand.layout} />
              ) : (
                <p className="text-xl opacity-70">
                  {current.notes ?? (current.kind === 'header' ? '' : 'No chart for this item.')}
                </p>
              )}
            </>
          )}
        </main>
      </div>

      {/* Long titles truncate so both buttons stay one line and easy to hit. */}
      <footer className="flex items-center justify-between gap-2 border-t border-white/10 px-2 py-2 sm:px-4">
        <button
          type="button"
          onClick={() => go(stand.index - 1)}
          disabled={stand.index === 0}
          aria-label={`Previous: ${stand.items[stand.index - 1]?.title ?? ''}`}
          className="flex max-w-1/2 min-w-0 items-center gap-2 rounded px-3 py-3 text-left text-base disabled:opacity-30 sm:px-4 sm:py-2 sm:text-lg"
        >
          <Icon icon={faArrowLeft} className="size-5" />
          <span className="truncate">{stand.items[stand.index - 1]?.title ?? ''}</span>
        </button>
        <button
          type="button"
          onClick={() => go(stand.index + 1)}
          disabled={stand.index >= stand.items.length - 1}
          aria-label={`Next: ${stand.items[stand.index + 1]?.title ?? ''}`}
          className="flex max-w-1/2 min-w-0 items-center gap-2 rounded px-3 py-3 text-right text-base disabled:opacity-30 sm:px-4 sm:py-2 sm:text-lg"
        >
          <span className="truncate">{stand.items[stand.index + 1]?.title ?? ''}</span>
          <Icon icon={faArrowRight} className="size-5" />
        </button>
      </footer>
    </div>
  )
}
